import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DailyRecord } from '@skd/shared';
import { INSPECTION_STAGES, INSPECTION_ITEMS, isItemApplicable } from '@skd/shared';
import { StatTile } from './StatTile';
import { DefectBarList, type DefectBarDatum } from './DefectBarList';
import { StatusChip } from '../ui/StatusChip';
import { Button } from '../ui/Button';

interface MainDashboardProps {
  records: DailyRecord[];
  selectedInvoiceNo: string;
  selectedRecordId: string | null;
  onSelectRecord: (id: string | null) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onDeleteRecord: (id: string) => void;
  canDeleteRecords: boolean;
  onOpenDefects: (recordId: string) => void;
}

function recordDate(record: any): Date | null {
  const raw = record.updatedAt || record.createdAt || record.timestamp;
  if (!raw) return null;
  // SQLite datetime('now') is UTC but omits the timezone suffix. Normalize
  // that format before converting it to the user's local calendar date.
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? `${raw.replace(' ', 'T')}Z`
    : raw;
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

function formatWhen(record: any): string {
  const d = recordDate(record);
  if (!d) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Local YYYY-MM-DD — matches what an <input type="date"> produces/expects. */
function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function recordDateKey(record: any): string | null {
  const d = recordDate(record);
  return d ? dateKey(d) : null;
}

function shiftDateKey(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const next = new Date(y, m - 1, d + days);
  return dateKey(next);
}

function formatDateKeyLong(key: string): string {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function MainDashboard({
  records,
  selectedInvoiceNo,
  selectedRecordId,
  onSelectRecord,
  searchQuery,
  onSearchChange,
  onDeleteRecord,
  canDeleteRecords,
  onOpenDefects,
}: MainDashboardProps) {
  const navigate = useNavigate();

  // "" means no date filter (all dates). A daily registry opens on the
  // user's local calendar day, even when there are no records today.
  const [selectedDate, setSelectedDate] = useState(() => dateKey(new Date()));

  const invoiceRecords = useMemo(() => {
    const invoice = selectedInvoiceNo.trim().toLowerCase();
    if (!invoice) return records;
    return records.filter((r) => r.metadata.invoice_no.trim().toLowerCase() === invoice);
  }, [records, selectedInvoiceNo]);

  const dateFilteredRecords = useMemo(() => {
    if (!selectedDate) return invoiceRecords;
    return invoiceRecords.filter((r) => recordDateKey(r) === selectedDate);
  }, [invoiceRecords, selectedDate]);

  const isSearching = searchQuery.trim().length > 0;

  // A search looks across the whole history, not just the selected day —
  // otherwise "which day was this body inspected on" becomes unanswerable.
  const filteredRecords = useMemo(() => {
    if (!isSearching) return dateFilteredRecords;
    const q = searchQuery.toLowerCase();
    return invoiceRecords.filter(
      (r) =>
        r.id.toLowerCase().includes(q) ||
        r.metadata.body_number.toLowerCase().includes(q) ||
        r.metadata.invoice_no.toLowerCase().includes(q)
    );
  }, [invoiceRecords, dateFilteredRecords, isSearching, searchQuery]);

  const stats = useMemo(() => {
    let totalChecked = 0;
    let totalPass = 0;
    let totalDefects = 0;
    const perSectionDefects: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const perItemDefects: Record<string, number> = {};

    for (const record of records) {
      for (const stage of INSPECTION_STAGES) {
        const stageData = record.stages[stage.id];
        if (!stageData) continue;
        for (const item of INSPECTION_ITEMS) {
          if (!isItemApplicable(item.id, stage.id)) continue;
          const itemData = stageData.items[item.id];
          if (!itemData) continue;
          totalChecked++;
          if (itemData.status === 'OK') totalPass++;
          else if (itemData.status === 'NG') {
            totalDefects++;
            perSectionDefects[stage.id] = (perSectionDefects[stage.id] || 0) + 1;
            perItemDefects[item.id] = (perItemDefects[item.id] || 0) + 1;
          }
        }
      }
    }

    const overallPassRate = totalChecked > 0 ? Math.round((totalPass / totalChecked) * 100) : 0;
    return { totalChecked, totalPass, totalDefects, perSectionDefects, perItemDefects, overallPassRate };
  }, [records]);

  const componentData: DefectBarDatum[] = useMemo(
    () =>
      INSPECTION_ITEMS.map((item) => ({
        id: item.id,
        code: `(${item.id})`,
        label: item.code,
        value: stats.perItemDefects[item.id] || 0,
      })),
    [stats.perItemDefects]
  );

  const sectionData: DefectBarDatum[] = useMemo(
    () =>
      INSPECTION_STAGES.map((stage) => ({
        id: String(stage.id),
        label: stage.shortName,
        value: stats.perSectionDefects[stage.id] || 0,
      })),
    [stats.perSectionDefects]
  );

  const verdictChip = (r: DailyRecord) =>
    r.defectCount === 0 ? (
      <StatusChip status="ok">Passed</StatusChip>
    ) : (
      <StatusChip status="ng">Action required</StatusChip>
    );

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      {/* KPI row */}
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Bodies" value={records.length} hint="inspected in total" />
        <StatTile label="Items checked" value={stats.totalChecked.toLocaleString()} hint="across all sections" />
        <StatTile
          label="Defects"
          value={stats.totalDefects}
          hint={stats.totalDefects === 0 ? 'none recorded' : `${stats.totalPass.toLocaleString()} passed`}
          chip={stats.totalDefects > 0 ? <StatusChip status="ng">NG</StatusChip> : undefined}
        />
        <StatTile
          label="Pass rate"
          value={`${stats.overallPassRate}%`}
          hint="of all checks"
          chip={
            stats.overallPassRate === 100 ? (
              <StatusChip status="ok">Clean</StatusChip>
            ) : undefined
          }
        />
      </section>

      {/* Registry */}
      <section className="card overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-[var(--border-color)] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="card-title">Daily Body Registry</h2>
            <p className="mt-0.5 text-[0.75rem] text-[var(--text-dim)]">
              {isSearching
                ? `${filteredRecords.length} match${filteredRecords.length === 1 ? '' : 'es'} for invoice ${selectedInvoiceNo || 'all'} across all dates`
                : `${filteredRecords.length} ${filteredRecords.length === 1 ? 'body' : 'bodies'}${selectedDate ? ` on ${formatDateKeyLong(selectedDate)}` : ''}${selectedInvoiceNo ? ` · Invoice ${selectedInvoiceNo}` : ''}`}
            </p>
          </div>
          <div className="relative sm:w-72">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-dim)]"
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              className="input-field pl-9"
              placeholder="Search body number or invoice"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {/* Day picker — narrows the list to one day at a time instead of the
            full history, with prev/next for quick browsing. Hidden while
            searching since a search already spans all history. */}
        {!isSearching && (
          <div className="flex items-center gap-1.5 border-b border-[var(--border-color)] bg-[var(--card-bg-subtle)] px-3 py-2">
            <button
              type="button"
              aria-label="Previous day"
              onClick={() => setSelectedDate((d) => shiftDateKey(d || dateKey(new Date()), -1))}
              className="btn btn-ghost btn-icon flex-shrink-0"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>

            <div className="relative flex-1 sm:max-w-[13rem]">
              <input
                type="date"
                aria-label="Select date"
                className="input-field"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            <button
              type="button"
              aria-label="Next day"
              onClick={() => setSelectedDate((d) => shiftDateKey(d || dateKey(new Date()), 1))}
              className="btn btn-ghost btn-icon flex-shrink-0"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>

            {selectedDate && (
              <button
                type="button"
                onClick={() => setSelectedDate('')}
                className="ml-1 flex-shrink-0 text-[0.75rem] font-medium text-[var(--accent-color)] underline-offset-2 hover:underline"
              >
                All dates
              </button>
            )}
          </div>
        )}

        {filteredRecords.length === 0 ? (
          <p className="p-8 text-center text-[0.8125rem] text-[var(--text-dim)]">
            {records.length === 0
              ? 'No bodies inspected yet. Start a new body form to get going.'
              : isSearching
              ? 'No records match that search.'
              : `No bodies inspected for invoice ${selectedInvoiceNo || 'selected'} on this date.`}
          </p>
        ) : (
          <>
            {/* Phone: card list — keeps verdict and actions on screen. Capped
                to roughly 5 rows; the rest scroll within the card instead of
                growing the page. */}
            <ul className="max-h-[30rem] divide-y divide-[var(--border-color)] overflow-y-auto md:hidden">
              {filteredRecords.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/records/${r.id}`)}
                    className="flex w-full flex-col gap-2 p-4 text-left transition-colors active:bg-[var(--card-bg-subtle)]"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[0.875rem] font-semibold text-[var(--text-main)]">
                        {r.metadata.body_number}
                      </span>
                      {verdictChip(r)}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.75rem] text-[var(--text-muted)]">
                      <span>{r.metadata.model}</span>
                      <span className="text-[var(--text-dim)]">·</span>
                      <span className="font-mono">Inv {r.metadata.invoice_no}</span>
                      <span className="text-[var(--text-dim)]">·</span>
                      <span className="tnum">{r.passRate}% pass</span>
                      {r.defectCount > 0 && (
                        <>
                          <span className="text-[var(--text-dim)]">·</span>
                          <span className="tnum text-[var(--status-ng-text)]">{r.defectCount} NG</span>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 text-[0.75rem] text-[var(--text-dim)]">
                      <span>{(r as any).inspectorName || 'Unassigned'}</span>
                      <span>·</span>
                      <span className="tnum">{formatWhen(r)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>

            {/* Desktop: full table, capped to roughly 5 rows with a sticky
                header, then scrolls within the card. */}
            <div className="hidden max-h-[19rem] overflow-auto md:block">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Body No</th>
                    <th>Model</th>
                    <th>Invoice</th>
                    <th>Inspector</th>
                    <th>Updated</th>
                    <th className="text-right">Checked</th>
                    <th className="text-right">NG</th>
                    <th className="text-right">Rate</th>
                    <th>Verdict</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => onSelectRecord(r.id === selectedRecordId ? null : r.id)}
                      className={`cursor-pointer ${selectedRecordId === r.id ? 'bg-[var(--accent-light)]' : ''}`}
                    >
                      <td className="font-mono font-semibold">{r.metadata.body_number}</td>
                      <td className="text-[var(--text-muted)]">{r.metadata.model}</td>
                      <td className="font-mono text-[var(--text-muted)]">{r.metadata.invoice_no}</td>
                      <td className="text-[var(--text-muted)]">{(r as any).inspectorName || '—'}</td>
                      <td className="tnum text-[var(--text-dim)]">{formatWhen(r)}</td>
                      <td className="text-right tnum">{r.totalChecked}</td>
                      <td className="text-right tnum">
                        {r.defectCount > 0 ? (
                          <button
                            type="button"
                            className="font-medium text-[var(--status-ng-text)] underline-offset-2 hover:underline"
                            onClick={(e) => { e.stopPropagation(); onOpenDefects(r.id); }}
                          >
                            {r.defectCount}
                          </button>
                        ) : (
                          <span className="text-[var(--text-dim)]">0</span>
                        )}
                      </td>
                      <td className="text-right tnum">{r.passRate}%</td>
                      <td>{verdictChip(r)}</td>
                      <td>
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/records/${r.id}`); }}
                          >
                            View
                          </Button>
                          {canDeleteRecords && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Delete ${r.metadata.body_number}`}
                              onClick={(e) => { e.stopPropagation(); onDeleteRecord(r.id); }}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
        <section className="card p-4 sm:p-5">
          <h2 className="card-title">Defects by component</h2>
          <p className="mb-4 mt-0.5 text-[0.75rem] text-[var(--text-dim)]">
            Ranked by number of NG findings across all bodies.
          </p>
          <DefectBarList data={componentData} />
        </section>

        <section className="card p-4 sm:p-5">
          <h2 className="card-title">Defects by section</h2>
          <p className="mb-4 mt-0.5 text-[0.75rem] text-[var(--text-dim)]">
            Where in the five-stage flow defects are being caught.
          </p>
          <DefectBarList data={sectionData} collapseZeros={false} />
        </section>
      </div>
    </div>
  );
}
